import React, {useEffect, useState} from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Container, Grid, Paper, Typography } from "@mui/material";
import { FileUpload as FileUploadIcon} from "@mui/icons-material";
import _ from 'lodash'
import Papa from 'papaparse';
import { read as readXLSX, utils } from "xlsx";
import Steppers from "./Steppers";
import Layout from "../../pages/Layout";
import pageStyles from "../../pages/pageStyles";
import MappingData  from "./steps/MappingData";
import ErrorMessageBox from "./ErrorMessage";
// import ConfirmLeaveDialog from "./ConfirmLeaveDialog";
import ConfirmImportDialog from "./ConfirmImportDialog";
import ActionButtons from "./ActionButtons";
import ShowResult from "./steps/ShowResult";
import ErrorChecking from "./steps/ErrorChecking";
import ServerErrorChecking from "./steps/ServerErrorChecking";
import { AxiosResponse } from "axios";
import { validateOnboardingData, getStagedTerminalData, importOnboardingData, getOnboardingID } from '../../repo/onboarding'
import useUserAccess from "../../effects/useUserAccess";
import posSchema from '../pos/posEdit/posEdit.schema';
import terminalSchema from '../terminal/terminalEditForm/terminalEditForm.schema';
import merchantSchema from '../merchant/merchantEditForm/merchantEditForm.schema';
import { isEmpty } from "ramda";
import { fetchRequest as fetchCustomGroups } from "../../store/customGroup/actions";
import { fetchRequest as fetchCard } from "../../store/card/actions";
import { fetchRequest as fetchEmv } from "../../store/emv/actions";
import {useDispatch, useSelector} from "react-redux";
import { ApplicationState } from "../../store";
import { Data as CustomGroupData } from "../../store/customGroup/types";
import { ContactType } from "../merchant/merchantEditForm/EditMerchantContactComponent";
import { fetchRequest as fetchPosSoftware } from "../../store/possoftware/actions";
import { Data as PosSoftwareData } from "../../store/possoftware/types";
import { loadOnboardingConfig } from "./onboardingConfig";
interface FieldValue {
  name: string;
  value: string;
}

export interface FormValues {
  fields: FieldValue[];
}

export enum Step {
  Uploading = 0,
  // ErrorChecking = 2,
  ServerErrorChecking = 1,
  Mapping = 2,
  Result = 3,
}

const stateToTimezoneMap = {
  NSW: "Australia/Sydney",
  QLD: "Australia/Brisbane",
  VIC: "Australia/Melbourne",
  TAS: "Australia/Hobart",
  NT: "Australia/Darwin",
  ACT: "Australia/Sydney",
  SA: "Australia/Adelaide",
  WA: "Australia/Perth",
};

const cardTypesMapping = {
  "VISA INTERNATIONAL": {
    "amount": "VisaInternationalAmount",
    "percentage": "VisaInternationalPercentage",
  },
  "VISA CREDIT": {
    "amount": "VisaCreditAmount",
    "percentage": "VisaCreditPercentage",
  },
  "VISA DEBIT": {
    "amount": "VisaDebitAmount",
    "percentage": "VisaDebitPercentage",
  },
  "MASTERCARD CREDIT": {
    "amount": "MastercardCreditAmount",
    "percentage": "MastercardCreditPercentage",
  },
  "MASTERCARD DEBIT": {
    "amount": "MastercardDebitAmount",
    "percentage": "MastercardDebitPercentage",
  },
  "MASTERCARD INTERNATIONAL": {
    "amount": "MastercardInternationalAmount",
    "percentage": "MastercardInternationalPercentage",
  },
  "AMEX": {
    "amount": "AmexAmount",
    "percentage": "AmexPercentage",
  },
  "AMEX INTERNATIONAL": {
    "amount": "AmexInternationalAmount",
    "percentage": "AmexInternationalPercentage",
  },
  "JCB": {
    "amount": "JcbAmount",
    "percentage": "JcbPercentage",
  },
  "DINERS": {
    "amount": "DinersAmount",
    "percentage": "DinersPercentage",
  },
  "UNIONPAY": {
    "amount": "UnionPayAmount",
    "percentage": "UnionPayPercentage",
  },
  "EFTPOS": {
    "amount": "EFTPOSAmount",
    "percentage": "EFTPOSPercentage",
  },
  "Motorpass": {
    "amount": "MotorpassAmount",
    "percentage": "MotorpassPercentage",
  },
}

const cleanedObject = (originalObject) => {
  return JSON.parse(JSON.stringify(originalObject))
}


const trimObject = (o) => {
  for (const [k, v] of Object.entries(o)) {
    if (Object(v) === v)
      trimObject(v)
    else if (typeof v === 'string')
      o[k] = v.trim();
  }
  return o;
}

const getTimeZone = (data) => {
  const listTZ = Object.keys(stateToTimezoneMap).map(i => stateToTimezoneMap[i]);
  if (listTZ.includes(data)) {
    return data
  }
  return undefined
}

const getValueInCents = (value) => {
  if (typeof value === 'number') {
    return value * 100
  }
  return !isNaN(value) ? Number(value.replace(',', '.') * 100) : undefined
}

export default function Onboarding() {
  const classes = pageStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [activeStep, setActiveStep] = useState(Step.Uploading);
  const [csvFile, setFile] = useState('');
  const [totalRow, setTotalRow] = useState(0);
  const [onboardingID, setOnboardingID] = useState('');
  const [onboardData, setData] = useState([]);
  const [dataToImport, setDataToImport] = useState([]);
  const [onboardError, setError] = useState([]);
  const [onboardServerError, setServerError] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateResult, setUpdateResult] = useState<AxiosResponse | undefined>(
    undefined
  );
  const [onboardingProfile, setOnboardingProfile] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    getOnboardingID().then(response => {
      setOnboardingID(response?.data?.id || '')
    })
    
    // Load onboarding config
    setConfigLoading(true);
    loadOnboardingConfig().then(config => {
      setOnboardingProfile(config);
      setConfigLoading(false);
    }).catch(error => {
      console.error('Failed to load onboarding config:', error);
      setConfigLoading(false);
    });
  }, [])

  const cpat = useSelector((state: ApplicationState) =>
    state.card.data ? state.card.data.data : []
  );
  const emvOptions =
    useSelector((state: ApplicationState) => state.emv.data) ?? [];

  // get all groups from state
  const allGroups =
    (useSelector<ApplicationState>(
      (store) => store.customGroup.data?.data
    ) as CustomGroupData[]) || [];

  const posSoftwareState = useSelector(
    (state: ApplicationState) => state.posSoftware
  );
  const posSoftware = posSoftwareState?.data?.data || ([] as PosSoftwareData[]);

  useEffect(() => {
    dispatch(fetchPosSoftware(null));
    if (isEmpty(allGroups)) {
      dispatch(fetchCustomGroups(null));
    }
    if (isEmpty(cpat)) {
      dispatch(fetchCard(null));
    }
    if (isEmpty(emvOptions)) {
      dispatch(fetchEmv());
    }
  }, []);

  const getPOSSoftwareByName = (name) => {
    if (name) {
      let software = _.find(posSoftware || [], (obj) => obj.attributes.ApplicationName === name);
      if (software) {
        return software.id
      }
      return null
    }
    return undefined
  }

  const handleConfirmImport = () => {
    setShowConfirmation(false);
    handleImport();
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const serverValidate = () => {
    handleNext();
    setIsLoading(true);
    // validate data with correct SerNo in DB
    const validateData = onboardData.filter(i => i.Terminal.id);
    validateOnboardingData(validateData).then(response => {
      let missingSerNo = onboardData.filter(i => !i.Terminal.id).map(i => {
        return {
          rowIndex: i.rowIndex,
          terminalError: {
            "SerNo": {
              message: 'Cannot Find this SerNo: ' + i.Terminal.attributes.SerNo
            }
          }
        }
      });
      let serverErrs = response
        .filter(i => i.merchantError || i.terminalError || i.posError)
        .concat(missingSerNo)
        .sort((a, b) => a.rowIndex - b.rowIndex)
      setServerError(serverErrs);
      setIsLoading(false);

    })
  }

  const handleUploadCsv = (event) => {
    setIsLoading(true);
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.type === 'text/csv') {
        Papa.parse(file, {
          header: true,
          complete: function(results) {
            setFile(file.name);
            setTotalRow(results.data.length);
            mappingDataToModel(results.data);
          }
        });
      } else {
        const reader = new FileReader();
        reader.onload = function(e) {
          let data = e.target.result;
          let workbook = readXLSX(data, {
            type: 'binary'
          });
          let sheetName = workbook.SheetNames[0]
          let XL_row_object = utils.sheet_to_json(workbook.Sheets[sheetName], {
            raw: false
          });
          setFile(file.name);
          setTotalRow(XL_row_object.length);
          mappingDataToModel(XL_row_object);
        };

        reader.onerror = function(ex) {
          console.log(ex);
        };

        reader.readAsBinaryString(file);
      }
    }
  }

  const validateSchema = async (result) => {
    let error = result.map((data) => {
      let merchantError = null;
      let terminalError = null;
      let posError = null;
      if (!data.Terminal.id) {
        return {
          rowIndex: data.rowIndex,
          terminalError: ['Cannot Find this SerNo: ' + data.Terminal.attributes.SerNo]
        }
      }
      try {
        merchantSchema.validateSync(data.Merchant, {abortEarly: false,});
      } catch (e) {
        merchantError = e.errors
      }
      try {
        terminalSchema.validateSync(data.Terminal, {abortEarly: false,});
      } catch (e) {
        terminalError = e.errors
      }
      try {
        posSchema.validateSync(data.POS, {abortEarly: false,});
      } catch (e) {
        posError = e.errors
      }

      if (merchantError || terminalError || posError) {
        return {
          merchantError,
          terminalError,
          posError,
          rowIndex: data.rowIndex
        }
      }
      return null
    });
    setError(error.filter(i => i));
  }

  // Function to deeply merge two objects
  const deepMerge = (target, source) => {
    for (const key in source) {
      if (source[key] instanceof Object && target[key] instanceof Object) {
        deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
    return target;
  };

  const mappingDataWithStagedData = (csvData, stageData) => {
    let res = [];
    res = csvData.map((row) => {
      let stageItem = stageData.find(i => i.attributes?.SerNo === row.Terminal?.attributes?.SerNo);
      if (stageItem) {
        delete stageItem?.attributes?.Applications;
        if (!Array.isArray(stageItem.attributes.Processor)) {
          stageItem.attributes.Processor = [];
        }

        // Safely handle ProcessorTemp
        if (row.Terminal?.attributes?.ProcessorTemp?.ProcessorName) {
          let processorIndex = stageItem.attributes.Processor.findIndex(i => i.ProcessorName == row.Terminal.attributes.ProcessorTemp.ProcessorName);
          if (processorIndex === -1) {
            stageItem.attributes.Processor.push(row.Terminal.attributes.ProcessorTemp);
          } else {
            stageItem.attributes.Processor[processorIndex] = {
              ...stageItem.attributes.Processor[processorIndex],
              ...row.Terminal.attributes.ProcessorTemp
            };
          }
          delete row.Terminal.attributes.ProcessorTemp;
        }

        // Safely handle Processor2Temp (if it exists)
        if (row.Terminal?.attributes?.Processor2Temp?.ProcessorName) {
          let processorIndex = stageItem.attributes.Processor.findIndex(i => i.ProcessorName == row.Terminal.attributes.Processor2Temp.ProcessorName);
          if (processorIndex === -1) {
            stageItem.attributes.Processor.push(row.Terminal.attributes.Processor2Temp);
          }
          delete row.Terminal.attributes.Processor2Temp;
        }

        // Safely handle Processor3Temp (if it exists)
        if (row.Terminal?.attributes?.Processor3Temp?.ProcessorName) {
          let processorIndex = stageItem.attributes.Processor.findIndex(i => i.ProcessorName == row.Terminal.attributes.Processor3Temp.ProcessorName);
          if (processorIndex === -1) {
            stageItem.attributes.Processor.push(row.Terminal.attributes.Processor3Temp);
          }
          delete row.Terminal.attributes.Processor3Temp;
        }

        if (row.POS?.attributes) {
          row.POS.attributes.Shard = stageItem.attributes.Shard;
        }
        
        return {
          ...row,
          Terminal: {
            id: stageItem.id,
            type: stageItem.type,
            attributes: {
              ...stageItem.attributes,
              ...row.Terminal.attributes,
            }
          }
        }
      }
      return row;
    });
    return res;
  }

  const mappingDataToModel = (data) => {
    let result = data.map((row, index) => {
      row = trimObject(row)
      if (row['SerNo']) {
        const MerchantContactFirstName = row['MerchantContactFirstName'] || '';
        const MerchantContactLastName = row['MerchantContactLastName'] || '';
        const MerchantTradingName = row['MerchantTradingName'] || '';
        let item = {
          Terminal: {
            TerminalType: row.TerminalType,
            MID: row.MID,
            TID: row.TID,
            SerNo: row.SerNo,
            MCC: String(row['MCC'] || ''),
            CPATName: row.CPATName,
            MCRMinAmount: getValueInCents(row['MCR Threshold']),
            MerchantReceiptPrint: row.MerchantReceiptPrint || 'Always',
            CustomerReceiptPrint: row.CustomerReceiptPrint || 'Always',
            Receipt: row['Customer Receipt Template'],
            ReceiptMerchant: row['Merchant Receipt Template'],
            WelfareRestriction: row.WelfareRestriction === 'Yes',
            TerminalGPSEnabled: row.TerminalGPSEnabled === 'Yes',
            ...(row['ProcessorName'] && {
              ProcessorTemp: {
                ReversalPending: false,
                CompletionPending: false,
                ProcessorName: row['ProcessorName'],
                TID: row['ProcessorTID'] ? row['ProcessorTID']: row['TID'],
                MID: row['ProcessorMID'] ? row['ProcessorMID']: row['MID'],
                STAN: '000001',
                AdditionalData: [],
                PPID: ['Nab', 'NabUPI'].includes(row['ProcessorName']) ? row['NabPPID'] : ""
              }
            }),
            ...(row['Processor2Name'] && {
              Processor2Temp: {
                ReversalPending: false,
                CompletionPending: false,
                ProcessorName: row['Processor2Name'],
                TID: row['Processor2TID'] ? row['Processor2TID']: row['TID'],
                MID: row['Processor2MID'] ? row['Processor2MID']: row['MID'],
                STAN: '000001',
                AdditionalData: [],
                PPID: ['Nab', 'NabUPI'].includes(row['Processor2Name']) ? row['NabPPID'] : ""
              }
            }),
            ...(row['Processor3Name'] && {
              Processor3Temp: {
                ReversalPending: false,
                CompletionPending: false,
                ProcessorName: row['Processor3Name'],
                TID: row['Processor3TID'] ? row['Processor3TID']: row['TID'],
                MID: row['Processor3MID'] ? row['Processor3MID']: row['MID'],
                STAN: '000001',
                AdditionalData: [],
                PPID: ['Nab', 'NabUPI'].includes(row['Processor3Name']) ? row['NabPPID'] : ""
              }
            }),
            Location : row['Location'],
            AddressState : row['State'],
            Address1 : row['Address1'],
            Address2 : row['Address2'],
            TZ : getTimeZone(row['TZ']),
            EMVConfigName: row['EMVConfigName'],
            EnableHTMLReceipts: row['EnableHTMLReceipts'] || 'No',
            Standalone: row['Standalone'] || undefined,
            CloudPOS: row['CloudPOS'] || 'Yes',
            POSApp: row['POSApp'] || 'Yes',
            FeatureCashOut: row['FeatureCashOut'] || 'No',
            FeaturePurWithCash: row['PurchaseWithCashOut'] || 'Yes',
            FeaturePurWithTip: row['PurchaseWithTip'] || 'No',
            FeatureRefund: row['FeatureRefund'] || 'Yes',
            FeatureMentorLite: row['FeatureMentorLite'] || 'Yes',
            FeatureMOTO: row['FeatureMOTO'] || 'Yes',
            FeaturePreAuth: row['FeaturePreAuth'] || 'No',
            TerminalStatusReportingEnabled: row['TerminalStatusReportingEnabled'] === 'Yes',
            IdleOnAmountEntry: row['IdleOnAmountEntry'] === 'Yes',
            FeatureCompletion: row['FeatureCompletion'] || 'No',
            FeatureRefundLimit: getValueInCents(row['RefundTransactionLimit']) || 99999999,
            FeatureRefundDailyLimit: getValueInCents(row['RefundDailyLimit']) || 99999999,
            FeatureManualSettlement: row['ManualSettlement'] || 'Yes',
            FeatureLastSettle: row['FeatureLastSettle'] || 'No',
            FeaturePreSettle: row['FeaturePreSettle'] || 'No',
            Note: row['Note'],
            PaymentMethods: [],
            URLUpgrade: row['URLUpgrade'] || undefined,
            URLUpgradeBackup: row['URLUpgradeBackup'] || undefined,
            URLUpgradeFirmware: row['URLUpgradeFirmware'] || undefined,
            POIID: null,
            ReversalManagedOnHost: row["ReversalManagedOnHost"] || undefined,
            TokenWalletFileConfigName: row["TokenWalletFileConfigName"] || undefined,
            FeaturePairWithPOSEnabled: row["FeaturePairWithPOSEnabled"] || undefined,
            USBPOS: row["USBPOS"] || undefined,
            URL: row["URL"] || undefined,
            URLBackup: row["URLBackup"] || undefined,
            IdleBitmap: row["BackgroundLogo"] || undefined,
            TempPassword: row["TempPassword"] || undefined,
            groups: row["groupId"]
              ? String(row["groupId"])
                  .split(',')
                  .map(s => {
                    const trimmed = s.trim();
                    // Only allow valid integer numbers, no leading zeros except for "0"
                    if (/^(0|[1-9]\d*)$/.test(trimmed)) {
                      return Number(trimmed);
                    }
                    return undefined;
                  })
                  .filter(n => n !== undefined)
              : undefined,
            // surcharge v2
            SurchargeV2Toggle: row.SurchargeV2Toggle || 'No',
            EnableSurchargeV2: row.EnableSurchargeV2 || 'No',
            SurchargeTip: row.SurchargeTip === 'Yes',
            SurchargeV2: row.SurchargeV2Toggle === 'Yes' ? [] : undefined,
            // tipping v2
            EnableTippingV2: row.EnableTippingV2 || 'No',
            TippingV2: row.EnableTippingV2 === 'Yes' ? {
              TippingMode: "",
              TipWithPercentage1: "",
              TipWithPercentage2: "",
              TipWithPercentage3: ""
            } : undefined,
            
            FixedSurchargePercentage: row.FixedSurchargePercentage || null,
            FixedSurchargeAmount: row.FixedSurchargeAmount || null,
            HashedHotCardEnabled: row.HashedHotCardEnabled || "No",
            IsHotcardDeltaDownloadAllowed: row.IsHotcardDeltaDownloadAllowed || "No"
          },
          Merchant: {
            MID: row['MID'],
            MCC: String(row['MCC'] || ''),
            Address1: row.Address1,
            Address2: '',
            State: row.State,
            Postcode: row.Postcode,
            Suburb: row.Suburb,
            Name: MerchantTradingName,
            CashOutDailyLimitEnabled: row.CashOutDailyLimitEnabled === 'Yes',
            CashOutDailyLimit: !isNaN(row.CashOutDailyLimit) ? Number(row.CashOutDailyLimit) : 0,
            FeatureMentorAccess: row['MentorAccess'] || 'Yes',
            MentorAccessUserName: row['MentorAccessUserName'],
            Contact: [{
              First: MerchantContactFirstName,
              Last: MerchantContactLastName,
              email: row['Merchant.Email'],
              Mobile: row['Merchant.Mobile'],
              Phone: row['Merchant.Phone'],
              SortNumber: ContactType.PRIMARY
            }],
          },
          POS: {
            POSDesc: row['POSDesc'],
            Interface: row['POSInterface'] || 'NEXO',
            PrintOnPOS: row.PrintOnPOS === 'Yes',
            PrintOnTerminal: row.PrintOnTerminal === 'Yes',
            TID: row.TID,
            POSSoftware: getPOSSoftwareByName(row['POSSoftwareName']),
            POIID: row.TID,
          }
        };
        // Apply profile-specific overrides.
        const userSuppliedProfile = row['Profile'];
        if (
          userSuppliedProfile && 
          typeof userSuppliedProfile === "string" && 
          userSuppliedProfile.trim() !== "" &&
          onboardingProfile && 
          onboardingProfile[userSuppliedProfile]
        ) {
          const profileAttributes = onboardingProfile[userSuppliedProfile].terminal.attributes;
          item.Terminal = deepMerge(profileAttributes, item.Terminal);
        }
        else if (userSuppliedProfile && configLoading) {
          console.log("Onboarding profile still loading, skipping profile override for: " + userSuppliedProfile);
        }
        else if (userSuppliedProfile && !onboardingProfile) {
          console.log("Onboarding profile failed to load, skipping profile override for: " + userSuppliedProfile);
        }
        else {
          console.log("No " + userSuppliedProfile + " profile or related config found");
        }
        // Loop through keys and values to dynamically populate AdditionalData.
        for (let i = 1; i <= 7; i++) {
          console.log("Adding Processor.AdditionalData");
          const key = row[`ProcessorAdditionalDataKey${i}`];
          const value = row[`ProcessorAdditionalDataValue${i}`];
          if (key && value && item.Terminal.ProcessorTemp) {
            item.Terminal.ProcessorTemp.AdditionalData.push({
              Encrypted: false,
              Tag: key,
              Value: value
            });
          }

          console.log("Adding Processor2.AdditionalData");
          const Processor2AdditionalDataKey = row[`Processor2AdditionalDataKey${i}`];
          const Processor2AdditionalDataValue = row[`Processor2AdditionalDataValue${i}`];
          if (Processor2AdditionalDataKey && Processor2AdditionalDataValue && item.Terminal.Processor2Temp) {
            item.Terminal.Processor2Temp.AdditionalData.push({
              Encrypted: false,
              Tag: Processor2AdditionalDataKey,
              Value: Processor2AdditionalDataValue
            });
          }

          console.log("Adding Processor3.AdditionalData");
          const Processor3AdditionalDataKey = row[`Processor3AdditionalDataKey${i}`];
          const Processor3AdditionalDataValue = row[`Processor3AdditionalDataValue${i}`];
          if (Processor3AdditionalDataKey && Processor3AdditionalDataValue && item.Terminal.Processor3Temp) {
            item.Terminal.Processor3Temp.AdditionalData.push({
              Encrypted: false,
              Tag: Processor3AdditionalDataKey,
              Value: Processor3AdditionalDataValue
            });
          }
        }
        if (item.Terminal.EnableSurchargeV2) {
          item.Terminal.SurchargeV2 = Object.keys(cardTypesMapping).map(cardType => {
            let amountField = cardTypesMapping[cardType].amount;
            let percentageField = cardTypesMapping[cardType].percentage;
            if (row[amountField]) {
              return {
                SurchargeType: "Amount",
                CardType: cardType,
                PurchaseSurchargeValue: String(row[amountField])
              }
            }
            if (row[percentageField]) {
              return {
                SurchargeType: "Percentage",
                CardType: cardType,
                PurchaseSurchargeValue: String(row[percentageField])
              }
            }
            return null
          }).filter(i => i)
        }
        if (item.Terminal.EnableTippingV2) {
          item.Terminal.TippingV2 = {
            TippingMode: row["TippingMode"] || "Percentage",
            TipWithPercentage1: String(row["TipWithPercentage1"]),
            TipWithPercentage2: String(row["TipWithPercentage2"]),
            TipWithPercentage3: String(row["TipWithPercentage3"])
          }
        }
        if (item.POS.Interface === 'NEXO') {
          item.Terminal.POIID = item.Terminal.TID
        }
        let scheduleJob = null;
        if (row['AutoSettlement'] === 'Yes') {
          scheduleJob = {
            enabled: true,
            timezone: stateToTimezoneMap[row.state || 'NSW'],
            timeSchedule: row['AutoSettlementTime']
          }
        }
        // payment method
        ['Manual', 'Contactless', 'Contact', 'Magnetic', 'QRCode'].map((key) => {
          if (row[key] === 'Yes' && !item.Terminal.PaymentMethods.includes(key)) {
            item.Terminal.PaymentMethods.push(key)
          }
        })
        Object.keys(row).filter(i => i.includes('Merchants.Contact')).map(key => {
          _.set(item, key, row[key])
        })
        // clean undefined field
        return {
          rowIndex: index,
          Terminal: {
            attributes: cleanedObject(item.Terminal)
          },
          Merchant: {
            attributes: cleanedObject(item.Merchant),
            type: "Merchant",
            scheduleJob
          },
          POS: {
            attributes: cleanedObject(item.POS),
            type: "POS"
          },
          OnboardingID: onboardingID
        };
      }
    }).filter(i => i);
    let listSerNo = result.map(i => i.Terminal.attributes.SerNo);
    getStagedTerminalData(listSerNo).then(response => {
      result = mappingDataWithStagedData(result, response);
      setData(result);
      validateSchema(result).then();
      setIsLoading(false);
    })
  }

  const handleShowConfirm = () => {
    const serverErrorList = onboardServerError.map(i => i.rowIndex);
    // filter out all row with errors
    let dataToImport = onboardData.filter(i => !serverErrorList.includes(i.rowIndex) && i.Terminal.id);
    setDataToImport(dataToImport)
    setShowConfirmation(true)
  }

  const handleImport = () => {
    handleNext()
    setIsLoading(true)
    importOnboardingData(dataToImport, {
      file: csvFile,
      totalRow: totalRow
    }).then(response => {
      setUpdateResult(response);
      setIsLoading(false);
    }).catch(e => {

    })
  }

  const steps = [
    "Upload CSV File",
    "Validation",
    // "Validate Schema",
    "Preview",
    "Summary",
  ];

  const getStepContent = (step: Step) => {
    switch (step) {
      case Step.Uploading:
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            width="100%"
            padding={4}
          >
            <Box width="100%" display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const link = document.createElement('a');
                  link.target = '_blank';
                  link.href = './onboarding/onboarding-template.xlsx';
                  link.click();
                }}
              >
                Download Template
              </Button>
            </Box>
            <h4>OnboardingID : <strong>{onboardingID}</strong></h4>
            <br/>
            <br/>
            <Typography>
              The following process will create new merchant records and update staged terminal records in bulk.
              <br/>
              You will need to review the data mapping and address any errors found by the system before confirming the import.
            </Typography>
            <br/>
            <br/>
            <div>
              <input
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                id="image-upload"
                type="file"
                onChange={handleUploadCsv}
              />
              <label htmlFor="image-upload">
                <span>{csvFile}</span>
                <Button style={{ margin: "10px" }} variant="contained" color="primary" component="span" startIcon={<FileUploadIcon />}>
                  Upload csv file
                </Button>
              </label>
            </div>
          </Box>
        );
      case Step.Mapping:
        return <MappingData isLoading={isLoading} onboardData={onboardData} />;
      // case Step.ErrorChecking:
      //   return <ErrorChecking isLoading={isLoading} onboardError={onboardError} />;
      case Step.ServerErrorChecking:
        return <ServerErrorChecking isLoading={isLoading} serverError={onboardServerError} />;
      case Step.Result:
        return <ShowResult isLoading={isLoading} updateResult={updateResult} />;
      default:
        return (
          <ErrorMessageBox message="Something wrong with the process. Please try again!" />
        );
    }
  };

  // Navigation functions
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <Layout sessionData={undefined}>
      <main className={classes.content as string}>
        <div className={classes.appBarSpacer as string} />
        <Container maxWidth="lg" className={classes.container as string}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper>
                <Steppers steps={steps} activeStep={activeStep} />
              </Paper>
              <Box my={2}>
                <Paper>
                  <Box className={classes.root as string}>{getStepContent(activeStep)}</Box>

                  <ActionButtons
                    csvFile={csvFile}
                    isLoading={isLoading}
                    activeStep={activeStep}
                    updateResult={updateResult}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    serverValidate={serverValidate}
                    setShowConfirmation={setShowConfirmation}
                    handleImport={handleShowConfirm}
                  />
                  <ConfirmImportDialog
                    open={showConfirmation}
                    onConfirm={handleConfirmImport}
                    onCancel={handleCancel}
                    dataLength={dataToImport.length}
                    totalRows={totalRow}
                    csvFile={csvFile}
                  />
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </main>
    </Layout>
  );
}
